import { PreviewType } from './customizer-editor';
import { IThumbList } from './editor-interfaces';
import {IKeyHole, IRepeatedImagePatternOptions} from "@/editor/base/editor.interfaces";

export interface IPage {
    edits: IPageEdits;
    elements: IEditorElement[];
    ice?: string;
    page_id: number;
    page_number: number;
    preview_image: string;
    project_id?: number;
    load_photos?: (() => Promise<void>)[];
}

export interface IConfigData {
    message_environment: string;
    ghost_opacity: number;
    max_image_size: number;
    personalizer_passthrough: string;
    default_language: string;
    static_url_root: string;
}

export interface IPageEdits {
    bleed: IBleed | null;
    pageName: string | null;
    canEdit: boolean;
    canReorder: boolean;
    canDelete: boolean;
    mode: string;
    type: string;
    width: number;
    height: number;
    keepInSafetyZone: boolean | null;
    spanningPage: boolean | null;
    joinedPage: boolean;
    keyHole: IKeyHole | null;
    freeObject: IFreeObject[];
    productType: string;
}

export interface IBleed {
    bleed_safety_t: number;
    bleed_safety_r: number;
    bleed_safety_b: number;
    bleed_safety_l: number;
    bleed_side_t: number;
    bleed_side_b: number;
    bleed_side_l: number;
    bleed_side_r: number;
    bleed_back_t: number;
    bleed_back_b: number;
    bleed_back_l: number;
    bleed_back_r: number;
    topSideDepth: number;
    bottomSideDepth: number;
    leftSideDepth: number;
    rightSideDepth: number;
    topBackOverPrint: number;
    bottomBackOverPrint: number;
    leftBackOverPrint: number;
    rightBackOverPrint: number;
    edge_style: string;
    edge_color: string;
    corner_type: string;
    edgeStyle: string;
    edgeColor: string;
    cornerType: string;
    backPresentation: string;

    //  wrap_options?: { edgeStyle: string; cornerType: string; backPresentation: string; };
}

export interface IEvent {
    e: Event;
    target?: any;
}

interface IObservable<T> {
    on(eventName: string, handler: (e: IEvent) => any): T;

    on(eventName: { [key: string]: Function }): T;

    trigger(eventName: string, options?: any): T;

    off(eventName?: string | any, handler?: (e: IEvent) => any): T;
}

export interface IFabricClass extends fabric.Object {
    extend?: (fns: any) => any;
    util?: fabric.IUtil;
    isLikelyNode: boolean;
}

export interface IEditorShape extends IFabricClass {
    uniquePhotoId?: number;
    linkedZoneId?: number;
    freeObjectId: number;
    previousRotation?: number;      // Only in NanoKiosk
    previousTouches?: TouchList;    // Only in NanoKiosk
    previousPinchZoom?: number;     // Only in NanoKiosk
    isEditing: boolean;

    photoObject: IEditorEditorPhoto;

    innerRotate(angle: number): void;

    styles: any;
    uid: string;
    effects: IEffects;
    preview_image?: string;
    x: number;
    y: number;

    getZoom(): number;

    getInnerRotation(): number;

    getStyle(style: string): string;

    setStyle(style: string, value: any): void;

    isGhosted(): boolean;

    ghost(): void;

    zoom(zoom: number): void;

    pan(deltaX: number, deltaY: number): void;

    rotatePhoto(angle: number): void;

    getMarginLeft(): number;

    getMarginRight(): number;

    getMarginTop(): number;

    getMarginBottom(): number;


    replaceImage(image: IEditorImage, reset: boolean, callback: () => void): void;
}

export interface IPhotoEdits {
    rotation: number;
    autocorrect: boolean;
}

export interface IPhoto {
    id: any;
    url?: string;
    edits: any;
    image_data: any;
    full_url?: string;
    width: number;
    height: number;
    filename?: string;
}

interface IEffects {
    blur: number;
    glowBlur: number;
    glowOpacity: number;
    glowColor: string;
    shadowBlur: any;
    shadowDistance: any;
    shadowAngle: number;
    shadowOpacity: number;
    shadowColor: string;
    reflectionBlur: any;
    reflectionOpacity: number;
    reflectionDistance: any;
    reflectionSize: any;
    duotoneColor: string;
    autoCorrect: any;
    vignetteBlur: any;
    vignetteColor: string;
    vignetteOpacity: number;
    filter: string | null;
    initialRotation: number;
    flipVertical: boolean;
    flipHorizontal: boolean;
    sharpness: number;
    blue: number;
    green: number;
    red: number;
    hue: number;
    luminance: number;
    saturation: number;
    contrast: number;
    brightness: number;
    rotation: number;
    redEyes: IPoint[];
    borderSize: number;
    borderColor: string;
    mask: any;
    autoRedEye: boolean;
}

interface IEditorImagePosition {
    x: number;
    y: number;
    width: number;
    height: number;
    zoom: number;
}

export interface IEditorElement {
    x: number;
    y: number;
    width: number;
    height: number;
    uid: string;
    freeObjectId: number;
    rotation: number;
    effects: IEffects;
    editable: boolean;
    edited?: boolean;
    text?: string;
    placeHolderTranslations: { [lang: string]: string };
    textEdits: any;
    tlf: any;
    locked: boolean;
    fixed: boolean;
    isLetterBoxed: boolean;
    order: number;
    frame: IEditorFrame;
    image: IEditorImage;
    imagePosition: IEditorImagePosition;
    rootStyle: any;
    styles: any;
    preview_image: string;
    textZoneId?: number;
    customerEditable?: boolean;

    getMarginLeft: () => number;
    getMarginTop: () => number;
    getMarginRight: () => number;
    getMarginBottom: () => number;

    generateUid: () => string;

    applyEffects(): void;

    zoom(zoomDelta: number, bestFit?: boolean): void;

    pan(dx: number, dy: number): void;

}

export interface IEditorElementError {
    type: number;
    page: number;
    label: string;
    extra: any;
}

export interface IEditorPage extends IObservable<IEditorPage>, IPage {
    new(): IEditorPage;

    id: number;
    number: number;
    type: string;
    width: number;
    height: number;
    elements: IEditorElement[];
    mode: string;
    name: string;
    index: number;
    groupIndex: number;
    canEdit: boolean;
    canReorder: boolean;
    canDelete: boolean;
    joined: boolean;
    spanning: boolean;
    isPreviewPage: boolean;
    errors: IEditorElementError[];
    bleed: any;
    isInsideFrontCover: boolean;
    isInsideBackCover: boolean;
    preview: string | HTMLImageElement;
    duplicateId: number;
    isLetterBoxed: boolean;
    productType: string;
    pagerLabel: string;

    isPortrait(): boolean;

    isDuplicate(): boolean;

    hasForeground(): boolean;

    setPreview(preview: any, selfQueue: object[]): void;

    setErrors(errors: any[]): void;

    getPreviewImageAsBase64(): string;

    getLabel(forPager?: boolean): string;

    getBackground(): IEditorBackgroundElement;

    getForeground(): IEditorForegroundElement;

    getPhotoIdsWithCount(o: Object): Object;

    addPage(page: IEditorPage): void;

    pages: IEditorPage[];

    emptyClone(): IEditorPage;
}

export interface IEditorBackgroundElement extends IEditorElement {
    new(image: IEditorImage, imagePosition?: IEditorImagePosition): IEditorBackgroundElement;
}

export interface IEditorForegroundElement extends IEditorElement {
    new(image: IEditorImage, imagePosition: IEditorImagePosition): IEditorForegroundElement;
}

export interface IFreeObject {
    freeObjectId: number;
    order: number;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    alpha: number;
    frameWindowX: number;
    frameWindowY: number;
    frameWindowWidth: number;
    frameWindowHeight: number;
    urlPrefix?: string;
    pathPrefix?: string;
    fullFrameFilename?: string;
    fullFrameWidth?: number;
    fullFrameHeight?: number;
    largeFrameFilename?: string;
    largeFrameWidth?: number;
    largeFrameHeight?: number;
    mediumFrameFilename?: string;
    mediumFrameWidth?: number;
    mediumFrameHeight?: number;
    smallFrameFilename?: string;
    smallFrameWidth?: number;
    smallFrameHeight?: number;
    canEdit: boolean;
    locked: boolean;
    fixed: boolean;
    isLetterBoxed?: boolean;
    textEdits?: ITextEdits;
    textZoneId?: number;
    linkedZoneId?: number;
    imageEdits?: IImageEdits;
    glowShadowReflection?: any;
    maskBorder?: IMaskBorder;
    vignette?: any;
    photoId: number;
    uniquePhotoId: number;
    graphicAssetId?: number;
    edgeId?: number;
    isClipArt: boolean;
    isBackground: boolean;
    isForeground: boolean;
    updated: boolean;
    customerEditable?: boolean;
    pattern?: IRepeatedImagePatternOptions;
}

export interface IMaskBorder {
    thickness: number;
    color: number;
    type: number;
}

export interface IImageEdits {
    urlPrefix: string;
    pathPrefix: string;
    smallImageFilename: string | null;
    smallImageWidth: number;
    smallImageHeight: number;
    mediumImageFilename: string | null;
    mediumImageWidth: number;
    mediumImageHeight: number;
    largeImageFilename: string | null;
    largeImageWidth: number;
    largeImageHeight: number;
    xlargeImageFilename: string;
    xlargeImageWidth: number;
    xlargeImageHeight: number;
    fullImageFilename: string;
    fullImageWidth: number;
    fullImageHeight: number;
    pictureBackColor: number;
    red: number | null;
    green: number | null;
    blue: number | null;
    brightness: number | null;
    contrast: number | null;
    sharpness: number | null;
    saturation: number | null;
    luminance: number | null;
    hue: number | null;
    picturePosX: number;
    picturePosY: number;
    zoom: number;
    zoomStep: number;
    rotation: number;
    autoCorrect: boolean;
    redEyes: IRedEye[];
    flipHorizontal: boolean;
    flipVertical: boolean;
    colorMode: number;
    duotoneColor: number;
}

export interface IRedEye extends IPoint {
    radius: number;
}

export interface IPoint {
    x: number;
    y: number;
}

export interface ISize {
    width: number;
    height: number;
}

export interface ICrop extends IPoint {
    width: number;
    height: number;
}

export interface ITextEdits {
    rootStyle?: {
        fill: string;
        fontFamily: string;
        fontSize: number;
        fontStyle: string;
        fontWeight: string;
        textAlign: string;
        textBackgroundColor: string;
        textDecoration: string;
        underline: boolean;

        lockFontColor: boolean;
        lockFontSize: boolean;
        lockTextAlign: boolean;
        lockTextStyle: boolean;
        maxCharacters: number;
        textTransform?: string;
    };
    text: string;
    edited: boolean;
    styles: any;
    preview_image?: string;
    preview_file?: string;
    placeHolderTranslations?: { [lang: string]: string };
    TLFText: string;
    originalHeight: number;
}

export interface IEditorElementCollection {
    width: number;
    height: number;
    pages: IPage[];
    visiblePages: IPage[];
    elements: IEditorElement[];
}

export interface IRenderer {
    render(collection: IEditorElementCollection, maxSize: number, done: (snaps: HTMLCanvasElement[]) => void, threeDPreview?: boolean, showDefaultText?: boolean): void;


    toPage(page: IPage): IEditorPage;

    toCollection(pages: IEditorPage[], showingIndex?: number): IEditorElementCollection;

    getEditorImageFromImage(img: HTMLImageElement, filename: string): IEditorImage;

    removeEditorImage(imageId: number): void;

    getEditorImage(imageId: number): IEditorImage;

    clearAllImages(): void;
}

export interface IProductPreview {
    destroy(): void;

    loadModel(threeDModel: string): void;

    loadPhotoFromBase64(dataUrl: string, flag: PreviewType): void;

    loadPhotoFromCanvas(canvas: HTMLCanvasElement, flag: PreviewType): void;

    loadPhotoFromImageSources(images: (HTMLCanvasElement | string)[], flag: PreviewType): void;
}

export interface IProductPreviewConstructor {
    new(el: HTMLElement, threeDPreviewColor?:string, distanceFactor?:number): IProductPreview;
}

export interface IEditorElementError {
    type: number;
    page: number;
    label: string;
    extra: any;
}

export interface IPageList {
    edits: null | IPageEdits;
    page_id: number;
    page_number: number;
    alignment?: number;
    preview_image: string;
}

export interface IMaskPreview {
    margin_left?: string;
    margin_right?: string;
    margin_top?: string;
    margin_bottom?: string;
    thumbs: IPreviewThumb[];
}

export interface INonPrintableBackgroundPreview {
    thumbs: IPreviewThumb[];
}

export interface IMaskPreviewData {
    mask: {
        L?: IMaskPreview;
        P?: IMaskPreview;
    };
    mask_bw: {
        L?: IMaskPreview;
        P?: IMaskPreview;
    };

    mask_merge: { [page: number]: IMaskPreview };
    overlay_merge: { [page: number]: IMaskPreview };
}

export interface INonPrintableBackgroundPreviewData {
    non_printable_background: {
        L?: INonPrintableBackgroundPreview;
        P?: INonPrintableBackgroundPreview;
    };
}

export interface IPreviewThumb {
    url: string;
    file_path: string;
    width: number;
    height: number;
    thumb_type: string;
    color?: string;
}

export interface IProjectResult {
    photo_project_id: number;
    cart_item_id: number;
    preview_url?: string;
}

export interface IUploadImageResult {
    thumb_list: IThumbList[];
    photo_id: number;
    url: string
}

export interface ICreateProjectResult {
    projectId: number;
    pageList: IPageList[];
    editorSubType: string;
    pageNames: string[];
    threeDColor?: string;
    threeDModel: string;
    templateName: string;
    productName: string;
    cartPreview: boolean;
    maskPreviewData: IMaskPreviewData;
    threeDOverlay: { url?: string, blend?: GlobalCompositeOperation };
    nonPrintableBackgroundPreviewData: INonPrintableBackgroundPreviewData;
    iteUrl: string;
    itePreview: string;
    itePagePreviews: { page: number, url: string, ite: string }[];
    warningErrorDetails: { error_zoom: number, warning_zoom: number };
    previewOutputFormat: string;
    productId: number;
    forceTextChange: boolean;
    showPreviewInForceModal: boolean;
    alwaysShowTermsAndConditions: boolean;
    previewOnSave: number;
    termsAndConditionsUrl: string;
    returnPolicyUrl: string;
}

export interface IEditorBackgroundConstructor {
    new(image: IEditorImage, options: any): IEditorBackground;
}


export interface IEditorFrame extends IEditorImage {
    marginLeft: number;
    marginTop: number;
    marginRight: number;
    marginBottom: number;
}

export interface IEditorPhotoGroupConstructor {
    new(image: IEditorImage, frame: IEditorFrame, options: any): IEditorPhotoGroup;
}

export interface IEditorPhotoGroup extends IEditorShape {
    photoObject: IEditorEditorPhoto;
    frameObject: IEditorEditorFrame;
    removePhoto(): void;
    unGhost(): void;
    isImageTooLarge(): boolean;
    drawWarningErrorContainer(ctx: CanvasRenderingContext2D, warningText: string, warningTopInitial: number,
                              warningLeftInitial: number, containerColor: string, fillColor: string, imageWidth: number,
                              textHeight?:number, fontName?:string):void;
    isImageWarning(): boolean;

}

export interface IEditorBackground extends IEditorPhotoGroup {
    onSpine: boolean;
    isLetterBoxed: boolean;
}

export interface IEditorForegroundConstructor {
    new(image: IEditorImage, options: any): IEditorForeground;
}

export interface IEditorForeground extends IEditorPhotoGroup {

}

export interface IEditorClipartConstructor {
    new(image: IEditorImage, options: any): IEditorClipart;
}

export interface IEditorClipart extends IEditorEditorImage {
    currentZoom: number;
}

export interface IEditorTextConstructor {
    new(text: string, options: any): IEditorText;
}

export interface IEditorText extends IEditorShape {
    addedByUser: boolean;
    edited: boolean;
    fontSize: number;
    text: string;
    underline: boolean;

    enterEditing(skipEmptyToDefault?: boolean): void;

    exitEditing(): void;

    setText(value: string): void;

    findBackgroundColor(): { r: number, g: number, b: number };

    _textLines: string[];
    autoFontSize?: boolean;

    autoFontSizeAdjust(): boolean;

    textBeforeEdit: string;
    originalFontSize?: number;
}

export interface IEditorFrameConstructor {
    new(frame: IEditorFrame, options: any): IEditorEditorFrame;
}

export interface IEditorEditorFrame extends IEditorEditorImage {

}

export interface IEditorEditorPhotoConstructor {
    new(image: IEditorImage, options: any): IEditorEditorPhoto;
}

export interface IEditorEditorPhoto extends IEditorEditorImage {

    elementAdded: boolean;
    angle: number;

    placePhoto(): void;

    getMinZoom(): number;
}

export interface IEditorEditorImageConstructor {
    new(name: string, options: any): IEditorEditorImage;
}

export interface IEditorEditorImage extends IEditorShape {
    _element: HTMLImageElement;
    _originalElement?: HTMLImageElement;
    optimalThumbLoaded: boolean;
    imagePosition: IEditorImagePosition;
    image: IEditorImage;

    getWidth(): number;

    getHeight(): number;

    hasAutoCorrect?(): boolean;

    hasAutoRedEye?(): boolean;

    setReady(ready: boolean, isOptimal: boolean): void;

    removeImage(): void;
    setAutoCorrect(autoCorrect: boolean, callback: () => void): void;
    setAutoRedEye(autoRedEye: boolean, callback: () => void): void;
    setWidth(val: number): void;
    setHeight(val: number): void;

}

export interface IStorefrontStaticCanvasConstructor {
    new(el?: HTMLElement, options?: any): IStorefrontStaticCanvas;
}

export interface IEditorOpenedFont {
    b?: string;
    i?: string;
    bi?: string;
    r?: string;
}

export interface IEditorFont {
    styles: string[];
    name: string;
    public: boolean;
    openedFont: IEditorOpenedFont;
    getImage: () => string;
    getCSS: () => string;
    getTTF: (style: string) => string;
    canItalicize: (isBold: boolean) => boolean;
    canBold: (isItalic: boolean) => boolean;
    hasStyle: (style: string) => boolean;
}

interface IEditorFontConstructor {
    new(): IEditorFont;
}

export interface IStorefrontStaticCanvas {
    contextContainer: CanvasRenderingContext2D;
    nodeCanvas: fabric.StaticCanvas;
    Font: IEditorFontConstructor;
}

export interface IThumbDictionary {
    [key: string]: IEditorThumb;
}

export interface IEditorThumb {
    width: number;
    height: number;
    name: string;
    image?: string;
}

export interface IEditorImageConstructor {
    new(id: number, urlPrefix: string, thumbs: IThumbDictionary, name?: string, height?: number, width?: number,
        edits?: { rotation: number, autocorrect: boolean }): IEditorImage;
}

export interface IEditorImage extends IEditorThumb {
    id: number;
    name: string;
    urlPrefix: string;
    pathPrefix: string;
    height: number;
    width: number;
    thumbs: IThumbDictionary;

    getThumb(thumb: string): IEditorThumb;

    getUrl(thumbName: string, hasAutoCorrect?: boolean, hasAutoRedEye?: boolean): string;

    edits: { rotation?: number, autocorrect?: boolean };
}

export interface IEditorGraphicAssetConstructor {
    new(id: number, name: string, urlPrefix: string, thumbs: IThumbDictionary): IEditorGraphicAsset;
}

export interface IEditorGraphicAsset extends IEditorImage {
    type: string;
}

export interface IEditorShapeAssetConstructor {
    new(jsonData: any): IEditorShapeAsset;
}

export interface IEditorShapeAsset extends IEditorShape {

}

export interface IEditorEdgeConstructor {
    new(id: number, urlPrefix: string, thumbs: IThumbDictionary, top: number, right: number, bottom: number, left: number): IEditorEdge;
}

export interface IEditorEdge extends IEditorImage {
    marginBottom: number;
    marginRight: number;
    marginLeft: number;
    marginTop: number;
}

export interface IEditorEditorGroup {
    _objects: fabric.Object[];

    _ungroupifyObject(object: fabric.Object): void;

    _groupifyObject(object: fabric.Object): void;

    top: number;
    left: number;
    width: number;
    height: number;
    index: number;
}

export interface IEditorGroupConstructor {
    new(objects: fabric.Object[], options: any): IEditorEditorGroup;
}

export interface IEditorCanvasConstructor {
    new(el: HTMLElement, options: any): IEditorCanvas;
}

export interface IEditor {
    PostRenderer: any;
    Options?: {
        EmptyImageFill?: string;
    };
    CenteringGuidelines: (canvas: IEditorCanvas) => void;
    Background: IEditorBackgroundConstructor;
    Foreground: IEditorForegroundConstructor;
    Clipart: IEditorClipartConstructor;
    Shape: IEditorShapeAssetConstructor;
    StaticCanvas: IStorefrontStaticCanvasConstructor;
    PhotoGroup: IEditorPhotoGroupConstructor;
    Image: IEditorEditorImageConstructor;
    Frame: IEditorFrameConstructor;
    Group: IEditorGroupConstructor;
    Photo: IEditorEditorPhotoConstructor;
    Text: IEditorTextConstructor;
    Canvas: IEditorCanvasConstructor;
}

export interface ICanvasErrorConfig {
    zoomWarning: number;
    zoomError: number;
}

export interface ITarget extends fabric.Object {
    scaleX: number;
    scaleY: number;
    skewX: number;
    skewY: number;
    left: number;
    top: number;
    width: number;
    height: number;
}

export interface ICurrentTransform {
    target: ITarget;
    action: string;
    corner: number;
    scaleX: number;
    scaleY: number;
    skewX: number;
    skewY: number;
    offsetX: number;
    offsetY: number;
    originX: string;
    originY: string;
    ex: number;
    ey: number;
    lastX: number;
    lastY: number;
    left: number;
    top: number;
    theta: number;
    width: number;
    mouseXSign: number;
    mouseYSign: number;
    shiftKey: number;
    altKey: number;
    original: {
        left: number;
        top: number;
        scaleX: number;
        scaleY: number;
        skewX: number;
        skewY: number;
        originX: number;
        originY: number;
    };
}

export interface IEditorObservable {
    trigger(eventName: string, options: any): IEditorObservable;

    on(eventName: string | any, handler: (options: any) => void): IEditorObservable;

    off(eventName: string | any, handler: (options: any) => void): IEditorObservable;
}

export interface IEditorPageGroupConstructor {
    new(pages?: IEditorPage[]): IEditorPage;
}

export interface IEditorPageGroup extends IEditorObservable {
    index: number;
    type: string;

    getObjects(): IEditorShape[];

    pagesJoined(value?: boolean): boolean;

    pagesSpanning(value?: boolean): boolean;

    canEdit(): boolean;

    canDelete(): boolean;

    canReorder(): boolean;

    pages: IEditorPage[];

    addPage(page: IEditorPage): void;
}

export interface IEditorCanvas extends fabric.StaticCanvas, Function {
    selection: boolean;
    objectsToLoad: number;
    fontService: any;
    threeDpreview: boolean;
    wrap: { [key: string]: any };
    lowerCanvasEl: HTMLCanvasElement;
    wrapperEl: HTMLElement;
    _previousMovePointer: any;
    canvasBackgroundColor?: string;
    visiblePages?: IEditorPage[];

    clipTo: (ctx: CanvasRenderingContext2D) => void;
    getSelectionContext(): CanvasRenderingContext2D;

    contextTop: CanvasRenderingContext2D;
    marginLeft: number;
    marginTop: number;
    marginRight?: number;
    marginBottom?: number;
    errorConfiguration: ICanvasErrorConfig;
    _currentTransform: ICurrentTransform;
    __gesturesParams?: any;

    _fire(eventName: string, options?: { target: any, event: any | null, disableUndoManager?: boolean }): void;

    getPointer(e: Event, ignoreZoom?: boolean, upperCanvasEl?: CanvasRenderingContext2D): { x: number; y: number; };

    _normalizePointer(activeObject: any, pointer: any): any;

    _checkTarget(pointer: { x: number, y: number }, object: fabric.Object, callSuper?: boolean): boolean;

    _setLocalMouse(localMouse: fabric.Point, t: ICurrentTransform): void;

    setActiveObject(object: Object, e?: Event): any;

    getActiveObject(): any;

    getActiveGroup(): IEditorPageGroup;

    keyhole: { [key: string]: any };

    _scaleObject(x: number, y: number, action: string): boolean;

    findTarget(e: Event, skipGroup?: boolean): fabric.Object;

    _setBackstoreDimension(field: string, value: number): void;

    discardActiveObject(): void;

}

export interface IToData {
    text: string;
    rootStyle: any;
    styles: Object[];
}

export interface ITLF {
    toData: (tlf: string) => IToData;
    toTLF: (data: IToData) => string;
}

export interface IEditorHelpers {
    clamp: (low: number, value: number, high: number) => number;
    zoomToFit: (innerWidth: number, innerHeight: number, outerWidth: number, outerHeight: number) => number;
    rotatePoint: (x: number, y: number, rotation: number) => number[];
    boundingBox: (angle: number, width: number, height: number, left: number, top: number, marginLeft: number, marginTop: number, marginRight: number, marginBottom: number) => number[];
    rotateImagePosition: (imagePosition: IEditorImagePosition, rotation: number) => void;
    supportsWebp: () => boolean;
    // setSelectionBorderColor: (lowerCtx: CanvasRenderingContext2D, coords: any, ratio: number) => string;
    isBrighterThan: (r: number, g: number, b: number, brightnessValue: number) => boolean;
    hasBackgroundObject: (canvas: IEditorCanvas) => boolean;
}

export interface IEditorErrorConfiguration {
    zoomWarning: number;
    zoomError: number;
}

export interface IElementResolution {
    OK: number;
    WARNING: number;
    ERROR: number;
}

export interface IEditorErrorDetector {
    getErrorsInPages(pages: IEditorPage[], pagesLength: number): IEditorElementError[];

    getErrorsInPage(page: IEditorPage, pagesLength: number, errorConfiguration: IEditorErrorConfiguration): IEditorElementError[];

    elementResolution: IElementResolution;
}

export interface IDatabaseBleed {
    leftSideDepth?: number;
    leftBackOverPrint?: number;
    rightSideDepth?: number;
    rightBackOverPrint?: number;
    topSideDepth?: number;
    topBackOverPrint?: number;
    bottomSideDepth?: number;
    bottomBackOverPrint?: number;

    edgeStyle?: string;
    edgeColor?: string;
    cornerType?: string;
    backPresentation?: string;
}

export interface IDatabaseEdits {
    mode: string;
    type: string;
    canEdit: boolean;
    canReorder: boolean;
    canDelete: boolean;
    joinedPage: boolean;
    width: number;
    height: number;
    freeObject: any;
    bleed: IDatabaseBleed;
    visiblePages: IEditorPage[];
    elements: IEditorElement[];
}

export interface IDatabasePage {
    edits: IDatabaseEdits;
    page_id: number;
    preview_image: string | null;
    page_number: number;
}

export interface IEditorEdits {

    pages?: IEditorPage[];

    // from edits to elements
    // toProject(data: any): IEditorProject;
    toPage(data: any, pageLabels?: string[], bleedData?: IDatabaseBleed): IEditorPage;

    validatePage(data: any): boolean;

    toElements(data: any[]): IEditorElement[];

    toElement(data: any): IEditorElement;

    // from elements to edits
    fromPage(page: IEditorPage): IDatabasePage;

    fromElement(elements: IEditorElement): IFreeObject;

    fromErrors(pages: IEditorPage[]): IEditorElementError[];
}

export interface IEditorRendererConstructor {
    new(fontService?: any, stateService?: any): IEditorRenderer;
}

export interface IEditorRenderer {
    createBoard(): IStorefrontStaticCanvas;

    createCanvas(width: number, height: number): HTMLCanvasElement;

    scaleBoard(width: number, height: number, scale: number): void;

    drawImage(context: CanvasRenderingContext2D, offset: number, width: number, height: number): void;

    setBoard(edits: IEditorElementCollection, showDefaultText?: boolean): void;

    render(edits: IEditorElementCollection, size: number, callback: (snapshots: HTMLCanvasElement[], selfQueue: object[]) => void, threeDPreview?: boolean, showDefaultText?: boolean): void;

    _board: IEditorCanvas;
}

export interface IImagePosition {
    x: number;
    y: number;
    width: number;
    height: number;
    zoom: number;
}

interface IEditorTranslator {
    boardSetter: (board: any, elementCollection: any, skipDefaultText: boolean) => void;
    toShape: (element: IEditorElement) => IEditorShape; // TODO: Make it to a Model.background
    toElement: (shape: IEditorShape) => IEditorElement;
    calculateOffset: (edits: { pages: IEditorPage[] }, pageIndex: number) => number;
    getElementImagePosition: (element: IEditorElement, forceRecalculate?: boolean) => IImagePosition;
    getImagePosition: (width: number, height: number, marginLeft: number, marginTop: number, marginRight: number, marginBottom: number, image: IEditorImage, rotation: number) => IImagePosition;
    cloneShape: (shape: IEditorShape) => IEditorShape;
    toImageModel: (photo: any, type?: string) => IEditorImage;
    buildShadow: (color: string, opacity: number, blur: number, distance: number, angle: number, strict?: boolean) => any;
    buildGlow: (color: string, opacity: number, blur: number, strict?: boolean) => any;
    buildRGBA: (color: string, opacity: number) => string;
    setOpacityInRGBA: (color: string, opacity: number) => string;
    rgb2hex: (rgbValue: string) => string;
    getOpacityFromRGBA: (rgba: string) => number;

    setSaveTextPreview(saveTextPreview: boolean): void;

    getSaveTextPreview(): boolean;

    resetSaveTextPreview(): void;
}

export interface IEditorFontService {
    fonts: IEditorFont[];
    get: (name: string) => IEditorFont | null;
    isLoaded: (name: string, style: string) => boolean;
    areLoaded: (fontDescriptions: { [fontFamily: string]: string[] }) => boolean;
    load: (font: IEditorFont, style: string) => Promise<void>;
    loadMultiple: (fontDescriptions: { [familyName: string]: string[] }) => Promise<any>;
    preLoad: (fontDescriptions: { [familyName: string]: string[] }) => void;
    getFirstLoadedFont: () => Promise<string | null>;
    loadFontFile?: () => Promise<void>;
    fontsLoaded: () => Promise<void>;
}

export interface ICustomizerFontApi {
    getFontData(): Promise<any[]>
}

interface IEditorCache {
    maxFullSizeImage: number;
}

export interface IEditorUtil {
    EventHandler: (callback: () => void) => () => void;
    TLF: ITLF;
    EditorHelpers: IEditorHelpers;
    Observable: IEditorObservable;
    ErrorDetector: IEditorErrorDetector;
    // Translator: IEditorTranslator;
    Edits: IEditorEdits;
    Renderer: IEditorRendererConstructor;
    // Math: IEditorMath;
    // Fonts: IEditorFontUtility;
    // Cache: IEditorCache;
    // ToolTip: IEditorTooltip;
    // MenuKeyboardListener: IMenuKeyboardListener;
    Translator: IEditorTranslator;
    KeyholeDrawer: IKeyHole;
    FontPreLoader: (pages: IEditorPage[], fontService: IEditorFontService) => void;
    GetFont?: (fontName: string) => any;
    IsAndroidKiosk: () => boolean;
    Cache: IEditorCache;
}

interface IVueComponent {
    template: string;
    data: () => any;
    name?: string;
    beforeMount?: () => void;
    mounted?: () => void;
    destroyed?: () => void;
    methods?: { [name: string]: (...args: any[]) => void };
    pageStart?: () => void;
    options?: { name: string };
}

interface IVueComponentDecoratorOptions {
    name?: string;
    showPageMenu?: boolean;
    template?: string;
    props?: {
        [key: string]: {
            type: StringConstructor | ObjectConstructor | NumberConstructor | ArrayConstructor | BooleanConstructor | FunctionConstructor | any[],
            default?: any
        }
    };
    computed?: { [key: string]: Function };
    components?: { [key: string]: IVueComponent };
    watch?: { [key: string]: (Function | { handler: Function, immediate?: boolean, deep?: boolean }) };
    abstract?: boolean;
    filters?: { [key: string]: Function };
}

export interface IVariant {
    id: number;
    name: string;
    price: number;
    public_title: string;
    sku: string;
}


export interface IStorefront {
    currentLanguage: string;
    personalizerPassThrough: string;
    staticUrlRoot: string;
    showTranslate: boolean;
    ghostOpacity: number;
    maxImageSize: number;
    Editor: IEditor;
    Util: IEditorUtil | {};
    Model: {
        ElementCollection: any;
        ProductAttribute: {
            BLEED_BACK_RIGHT: number;
            BLEED_BACK_LEFT: number;
            BLEED_BACK_TOP: number;
            BLEED_BACK_BOTTOM: number;
            BLEED_SIDE_RIGHT: number;
            BLEED_SIDE_LEFT: number;
            BLEED_SIDE_TOP: number;
            BLEED_SIDE_BOTTOM: number;
        };
        EdgeStyles: {
            BORDER: string;
            BORDER_WHITE: string;
            EXTEND: string;
            IMAGE_WRAP: string;
            IMAGE_WRAP_WITH_BLUR: string;
            MIRROR_WRAP: string;
            MIRROR_WRAP_WITH_BLUR: string;
        }
        WrapOptions: {
            CORNER_TYPE: string;

        };
        CornerTypes: {
            DIAGONAL_FOLD: string;
            VERTICAL_FOLD: string;
            HORIZONTAL_FOLD: string;

        };
        TextElement: any;
        PhotoElement: any;

    };
    tr: (phraseKey: string, replacements?: {
        [p: string]: number | string
    } | string, defaultTranslation?: string, value?: boolean) => string;
    Preview: {
        ProductPreviewer: IProductPreviewConstructor;
    };
    addEventListener: (event: string | string[], selector: string, callback: (ev: Event, el: HTMLElement) => void) => void;
    removeEventListener: (eventType: string, querySelector: string) => void;

    onReady(callback: () => void): void;

    trWithCount(count: number, phraseKey: string, replacements: { [key: string]: string | number },
                pluralPhraseKey: string, enableTranslations: boolean, defaultTranslation: string): string;
}

declare global {
    interface Window {
        // Used in the customizer
        SFLanguage: { [key: string]: string };

        isEnteringText: boolean;

        WebFont: any;
        SF: IStorefront;

        // Used in shopify-admin
        // Used in tests
        isAdvancedOrder: boolean;
        partnerSku: string;
        validateJavascript: boolean;


        // Used in shopify-admin
        Shopify: {
            allowChoosePreviews: boolean;
            staticUrlRoot: string;
            apiKey: string;
            userId: string;
            shippingZoneName: string;
            newJsonPath: string;
            shopifyJwt: string;
            maxImageSize: number;
            customPricing: boolean;
            shop: string;
        };

        ShopifyAnalytics: {
            meta: {
                product: {
                    gid: string;
                    id: number;
                    type: string;
                    vendor: string;
                    variants: IVariant[];
                }
                selectedVariantId: string;
                page: { pageType: string };
            }
        };

        VueClassComponent: {
            default: (options?: IVueComponentDecoratorOptions) => any;
            createDecorator: (factory: (componentOptions: IVueComponentDecoratorOptions, handler: Function) => void) => any;
        };

    }
}

// Constants export
export const SF: IStorefront = window.SF;
export const _: {
    template(html: string): (options: any) => string;
    cloneDeep(obj: any): any;
    isEquals(a: any, b: any): boolean;
} = (window as any)._;


export const fabric: {
    Object: {},

    IUtil: {},

    StaticCanvas: {},

    Point: {},

    isTouchSupported: boolean,
} = (window as any).fabric;


export const storefrontAnalytics: {
    trackEvent: (category: string, action: string, name: string, value: string) => void;
} = (window as any).storefrontAnalytics;

export type GetPageErrorType = 'textPreview'|'others';
